import { type SportsbookSocketManager } from './SportsbookSocketManager';

export class SportsbookSocketCascadeManager {
  rootModel: TCascadeModels;
  rootModelIds: string[];
  targetModel: TCascadeTargetModels;
  subscriberId: string;

  sportsbookSocketManager: SportsbookSocketManager;

  lineIds: Set<string> = new Set();
  disciplineIds: Set<string> = new Set();
  tournamentIds: Set<string> = new Set();
  eventIds: Set<string> = new Set();
  marketIds: Set<string> = new Set();

  constructor({
    rootModel,
    rootModelIds,
    targetModel,
    socketInstance,
    key,
  }: ISportsbookCascadeManagerParams) {
    if (!['line', 'discipline', 'tournament'].includes(rootModel)) {
      throw new Error(`Root model ${rootModel} is not allowed for cascade subscription`);
    }

    this.rootModel = rootModel;
    this.rootModelIds = rootModelIds;
    this.targetModel = targetModel;

    this.sportsbookSocketManager = socketInstance;

    const customKey = key ? `cascade_${key}` : 'cascade';
    const { subscribeId } = this.sportsbookSocketManager.registerSubscriber({ key: customKey });

    this.subscriberId = subscribeId;
  }

  initCascadeSubscription() {
    switch (this.rootModel) {
      case 'line':
        this.lineIds = new Set(this.rootModelIds);
        break;
      case 'discipline':
        this.disciplineIds = new Set(this.rootModelIds);
        break;
      case 'tournament':
        this.tournamentIds = new Set(this.rootModelIds);
        break;
    }

    const { on, subscribe } = this.sportsbookSocketManager.scope(this.subscriberId);

    on('line', this.lineHandler);
    on('discipline', this.disciplineHandler);
    on('tournament', this.tournamentHandler);

    subscribe(this.rootModel, this.rootModelIds);
  }

  lineHandler = (payload: TCascadeUpdatePayload<LineValue>) => {
    if (payload.type === 'leave') {
      this.lineIds.delete(payload.data.value);

      if (this.rootModel === 'line' && this.lineIds.size === 0) {
        this.destroy();
      }

      return;
    }

    const { disciplineIds = [] } = payload.data.value;

    this.disciplineIds = new Set([...this.disciplineIds, ...disciplineIds]);

    const { subscribe } = this.sportsbookSocketManager.scope(this.subscriberId);

    subscribe('discipline', [...this.disciplineIds]);
  };

  disciplineHandler = (payload: TCascadeUpdatePayload<DisciplineValue>) => {
    if (payload.type === 'leave') {
      this.disciplineIds.delete(payload.data.value);

      if (this.rootModel === 'discipline' && this.disciplineIds.size === 0) {
        this.destroy();
      }

      return;
    }

    if (this.targetModel === 'discipline') {
      return;
    }

    const { tournamentIds = [] } = payload.data.value;

    this.tournamentIds = new Set([...this.tournamentIds, ...tournamentIds]);

    const { subscribe } = this.sportsbookSocketManager.scope(this.subscriberId);

    subscribe('tournament', [...this.tournamentIds]);
  };

  tournamentHandler = (payload: TCascadeUpdatePayload<TournamentValue>) => {
    if (payload.type === 'leave') {
      this.tournamentIds.delete(payload.data.value);

      if (this.rootModel === 'tournament' && this.tournamentIds.size === 0) {
        this.destroy();
      }

      return;
    }

    if (this.targetModel === 'tournament') {
      return;
    }

    const { eventIds = [] } = payload.data.value;
    this.eventIds = new Set([...this.eventIds, ...eventIds]);

    const { subscribe } = this.sportsbookSocketManager.scope(this.subscriberId);

    subscribe('event', [...this.eventIds]);
  };

  destroy = () => {
    this.disciplineIds.clear();
    this.eventIds.clear();
    this.lineIds.clear();
    this.tournamentIds.clear();

    this.sportsbookSocketManager.unregisterSubscriber(this.subscriberId);
  };
}

export type TCascadeModels = 'line' | 'discipline' | 'tournament';
export type TCascadeTargetModels = TCascadeModels | 'event';

export interface ISportsbookCascadeManagerParams {
  rootModel: TCascadeModels;
  rootModelIds: string[];
  targetModel: TCascadeTargetModels;
  socketInstance: SportsbookSocketManager;
  key?: string | undefined;
}

interface LineValue {
  lid: string;
  disciplineIds?: string[];
}

interface DisciplineValue {
  lid: string;
  tournamentIds: string[];
}

interface TournamentValue {
  lid: string;
  eventIds: string[];
}

type TCascadeUpdatePayload<TBatch> =
  | {
      type: 'batch';
      data: {
        value: TBatch;
      };
    }
  | {
      type: 'leave';
      data: { value: string };
    };
